import { useState, useCallback, useMemo, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import { OTHER_CHARGES_INVOICE } from '@constants'
import { saveInvoice, buildBaseInvoicePayload } from '../BasicDetails/invoiceApi'

/**
 * Builds the staged `charges` object ({ [chargeKey]: amount }) from the flat
 * charge keys the invoice stores at top level (freight_charge, cutting_charge…).
 */
const buildCharges = (invoiceData) =>
	OTHER_CHARGES_INVOICE.reduce((acc, { key }) => {
		const value = invoiceData?.[key]
		if (value !== null && value !== undefined && value !== '') acc[key] = value
		return acc
	}, {})

/**
 * Owns the staged other-charges for the Other Charges card. Edit mode,
 * dirty-check, and save are independent from the Serviced Quantity card
 * (useServicedQuantityItems) - each card saves only its own slice of the invoice.
 */
const useOtherCharges = (supplierPoNumber, update) => {
	const dispatch = useDispatch()
	const invoiceData = useSelector((state) => state.getIn(['root', 'drawerTypeMeta', 'invoiceData']))
	const order = useSelector((state) => state.getIn(['root', 'drawerTypeMeta', 'order']))

	const [isEditMode, setIsEditMode] = useState(false)
	const [isSaving, setIsSaving] = useState(false)
	const [charges, setCharges] = useState(() => buildCharges(invoiceData))

	// Keep local copy in sync when the invoice in the drawer changes (e.g. after a save).
	useEffect(() => {
		setCharges(buildCharges(invoiceData))
	}, [invoiceData])

	const enterEditMode = useCallback(() => {
		setCharges(buildCharges(invoiceData))
		setIsEditMode(true)
	}, [invoiceData])

	const addCharge = useCallback((key, amount) => {
		setCharges((prev) => ({ ...prev, [key]: amount }))
	}, [])

	const removeCharge = useCallback((key) => {
		setCharges((prev) => {
			const next = { ...prev }
			delete next[key]
			return next
		})
	}, [])

	const initialCharges = useMemo(() => buildCharges(invoiceData), [invoiceData])

	const isDirty = useMemo(() => {
		const initialKeys = Object.keys(initialCharges)
		const currentKeys = Object.keys(charges)
		return (
			initialKeys.length !== currentKeys.length ||
			currentKeys.some((k) => String(charges[k] ?? '') !== String(initialCharges[k] ?? ''))
		)
	}, [charges, initialCharges])

	const handleUpdate = useCallback(async () => {
		setIsSaving(true)

		// Only send charge keys that are relevant: present ones with their amount,
		// previously-saved ones that were removed (null to clear), never-set ones omitted.
		const savedCharges = buildCharges(invoiceData)
		const chargesPayload = OTHER_CHARGES_INVOICE.reduce((acc, { key }) => {
			const value = charges[key]
			if (value !== undefined && value !== '' && value !== null) {
				acc[key] = Number(value)
			} else if (key in savedCharges) {
				acc[key] = null
			}
			return acc
		}, {})

		const payload = {
			...buildBaseInvoicePayload(invoiceData, order, supplierPoNumber),
			gps_link: invoiceData?.gps_link,
			...chargesPayload,
		}

		const ok = await saveInvoice({ payload, fallbackInvoice: invoiceData, order, invoiceData, dispatch, supplierPoNumber, update })
		if (ok) setIsEditMode(false)
		setIsSaving(false)
	}, [charges, invoiceData, order, dispatch, supplierPoNumber, update])

	return {
		charges,
		isEditMode,
		isSaving,
		isDirty,
		addCharge,
		removeCharge,
		enterEditMode,
		handleUpdate,
		setIsEditMode,
	}
}

export default useOtherCharges
